import { cellText, rawValue } from "./cellValue";
import type { ActiveFilters, Column } from "./DataTableTypes";
import { filterSpec, NO_FILTER } from "./filterOptions";
import { normalizeText } from "./normalizeText";

/** Keeps the texts of different cells apart, so a query never matches across two cells. */
const SEARCH_FIELD_SEPARATOR = " \u0001 ";

export interface RowCriteria<R> {
  columns: Column<R>[];
  query: string;
  filters: ActiveFilters;
  searchText?: (row: R) => string;
}

/** Value a filter compares with: the raw value for fixed options, otherwise the cell text. */
function filterValue<R>(row: R, col: Column<R>): string {
  return filterSpec(col).options ? String(rawValue(row, col)) : cellText(row, col);
}

export function matchesFilters<R>(row: R, columns: Column<R>[], filters: ActiveFilters): boolean {
  return Object.entries(filters)
    .filter(([, value]) => value !== NO_FILTER)
    .every(([key, value]) => {
      const col = columns.find((c) => c.key === key);
      return !col || filterValue(row, col) === value;
    });
}

function searchHaystack<R>(row: R, { columns, searchText }: RowCriteria<R>): string {
  return columns
    .filter((c) => c.type !== "actions")
    .map((c) => cellText(row, c))
    .concat(searchText ? [searchText(row)] : [])
    .join(SEARCH_FIELD_SEPARATOR);
}

/** The rows that pass every active filter and contain the query, ignoring case and accents. */
export function filterRows<R>(rows: R[], criteria: RowCriteria<R>): R[] {
  const needle = normalizeText(criteria.query.trim());
  return rows.filter(
    (row) =>
      matchesFilters(row, criteria.columns, criteria.filters) &&
      (!needle || normalizeText(searchHaystack(row, criteria)).includes(needle)),
  );
}

/** True when a filter or the search narrows the rows. */
export function isNarrowed(filters: ActiveFilters, query: string): boolean {
  return Object.values(filters).some(Boolean) || query.trim() !== "";
}
